/**
 * Specialists List Page
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  Avatar,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { PageHeader, ConfirmDialog } from '../../components/common';
import {
  useGetSpecialistsQuery,
  useDeleteSpecialistMutation,
  useActivateSpecialistMutation,
  useDeactivateSpecialistMutation,
} from '../../store/api/specialistsApi';
import type { Specialist } from '../../types/user';
import moment from 'moment';
import toast from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';
import ProviderListFilters from '../../components/providers/ProviderListFilters';
import type { ProviderListFilters as ProviderFilters } from '../../types/providerProfile';

const SpecialistsListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canManageSpecialists = hasPermission('manage_specialists');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ProviderFilters>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSpecialist, setSelectedSpecialist] = useState<Specialist | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const [deleteSpecialist, { isLoading: isDeleting }] = useDeleteSpecialistMutation();
  const [activateSpecialist] = useActivateSpecialistMutation();
  const [deactivateSpecialist] = useDeactivateSpecialistMutation();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Specialists query
  const { data: specialistsData, isLoading: specialistsLoading } = useGetSpecialistsQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    ...filters,
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, specialist: Specialist) => {
    setAnchorEl(event.currentTarget);
    setSelectedSpecialist(specialist);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSpecialist(null);
  };

  const handleDelete = async () => {
    if (!selectedSpecialist) return;

    try {
      await deleteSpecialist(selectedSpecialist.id).unwrap();
      toast.success(t('specialists.toast.deleted'));
      setDeleteDialog(false);
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.deleteFailed'));
    }
  };

  const handleActivate = async (specialist: Specialist) => {
    try {
      await activateSpecialist(specialist.id).unwrap();
      toast.success(t('specialists.toast.activated'));
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.activateFailed'));
    }
  };

  const handleDeactivate = async (specialist: Specialist) => {
    try {
      await deactivateSpecialist(specialist.id).unwrap();
      toast.success(t('specialists.toast.deactivated'));
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.deactivateFailed'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const specialistsColumns = [
    { id: 'id', label: t('specialists.cols.id'), minWidth: 80 },
    { id: 'profile', label: t('specialists.cols.profile'), minWidth: 150 },
    { id: 'full_name', label: t('specialists.cols.name'), minWidth: 150 },
    { id: 'email', label: t('specialists.cols.email'), minWidth: 150 },
    { id: 'phone_number', label: t('specialists.cols.phone'), minWidth: 120 },
    { id: 'age', label: t('specialists.cols.age'), minWidth: 80 },
    { id: 'specialization', label: t('specialists.cols.specialization'), minWidth: 150 },
    { id: 'service_areas', label: t('providers.cols.areas'), minWidth: 160 },
    { id: 'status', label: t('specialists.cols.status'), minWidth: 100 },
    { id: 'is_active', label: t('specialists.cols.active'), minWidth: 80 },
    { id: 'rating', label: t('specialists.cols.rating'), minWidth: 100 },
    { id: 'total_orders', label: t('specialists.cols.orders'), minWidth: 80 },
    { id: 'created_at', label: t('specialists.cols.created'), minWidth: 120 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('nav.specialists')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.specialists') },
        ]}
        {...(canManageSpecialists
          ? {
              actionLabel: t('specialists.buttons.add'),
              actionIcon: <AddIcon />,
              onAction: () => navigate('/specialists/create'),
            }
          : {})}
      />

      {/* Search Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            placeholder={t('specialists.filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <ProviderListFilters
            value={filters}
            onChange={(next) => {
              setFilters(next);
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Specialists Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {specialistsColumns.map((column) => (
                  <TableCell key={column.id} sx={{ fontWeight: 600 }}>
                    {column.label}
                  </TableCell>
                ))}
                {canManageSpecialists && (
                  <TableCell sx={{ fontWeight: 600, width: 50 }}>{t('common.actions')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {specialistsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {specialistsColumns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    ))}
                    {canManageSpecialists && (
                      <TableCell>
                        <Skeleton variant="circular" width={40} height={40} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : specialistsData?.results && specialistsData.results.length > 0 ? (
                specialistsData.results.map((specialist: Specialist) => (
                  <TableRow
                    key={specialist.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/specialists/${specialist.id}`)}
                  >
                    <TableCell>{specialist.id}</TableCell>
                    <TableCell>
                      <Avatar
                        src={specialist.profile_picture || undefined}
                        alt={specialist.full_name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {specialist.full_name.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>{specialist.full_name}</Box>
                        {specialist.is_verified && (
                          <Chip
                            label={t('specialists.badge.verified')}
                            size="small"
                            color="success"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{specialist.email || '—'}</TableCell>
                    <TableCell>{specialist.phone_number}</TableCell>
                    <TableCell>{specialist.age || t('specialists.na')}</TableCell>
                    <TableCell>{specialist.specialization || '—'}</TableCell>
                    <TableCell>
                      {[...(specialist.service_cities ?? []), ...(specialist.service_areas ?? [])].join('، ') || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`specialists.status.${specialist.status}`)}
                        size="small"
                        color={getStatusColor(specialist.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={specialist.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                        label={specialist.is_active ? t('specialists.status.ACTIVE') : t('specialists.status.INACTIVE')}
                        size="small"
                        color={specialist.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>⭐</span>
                        <span>{parseFloat(specialist.rating).toFixed(1)}</span>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>
                          ({specialist.total_reviews})
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell>{specialist.total_orders}</TableCell>
                    <TableCell>
                      {moment(specialist.created_at).format('MMM DD, YYYY')}
                    </TableCell>
                    {canManageSpecialists && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, specialist)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={specialistsColumns.length + (canManageSpecialists ? 1 : 0)}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('specialists.empty')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {specialistsData && (
          <TablePagination
            component="div"
            count={specialistsData.count || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedSpecialist && (
          <>
            <MenuItem
              onClick={() => {
                navigate(`/specialists/${selectedSpecialist.id}`);
                handleMenuClose();
              }}
            >
              <ViewIcon sx={{ mr: 1 }} fontSize="small" />
              {t('specialists.buttons.viewDetails')}
            </MenuItem>
            {canManageSpecialists && (
              <>
                <MenuItem
                  onClick={() => {
                    navigate(`/specialists/${selectedSpecialist.id}/edit`);
                    handleMenuClose();
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('common.edit')}
                </MenuItem>
                {selectedSpecialist.is_active ? (
                  <MenuItem
                    onClick={() => {
                      handleDeactivate(selectedSpecialist);
                    }}
                  >
                    <CancelIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('specialists.buttons.deactivate')}
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={() => {
                      handleActivate(selectedSpecialist);
                    }}
                  >
                    <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('specialists.buttons.activate')}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setDeleteDialog(true);
                    handleMenuClose();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('common.delete')}
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('specialists.dialog.deleteTitle')}
        message={t('specialists.dialog.deleteMessage', { name: selectedSpecialist?.full_name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={isDeleting}
      />
    </Box>
  );
};

export default SpecialistsListPage;

